import { Injectable } from '@angular/core';
import { Logger } from '@app/@shared/logger.service';
import {
  LangChangeEvent,
  TranslatePipe,
  TranslateService,
} from '@ngx-translate/core';
import { LocalStorageService } from 'ngx-webstorage';
import { BehaviorSubject, Subscription } from 'rxjs';

const log = new Logger('I18nService');
const languageKey = 'language';

export const DEFAULT_LANGUAGE = 'it_IT';
export const LANGUAGES_API_URL =
  'https://lcert-lokalise-pr-bucket.s3.eu-west-1.amazonaws.com/mysign/';
export const LANGUAGES_LOCAL_URL = './assets/translations';

export const languagesMap = {
  en: 'en_US',
  it: 'it_IT',
  es: 'es_ES',
  de: 'de_DE',
  fr: 'fr_FR',
  hu: 'hu_HU',
  pl: 'pl_PL',
  pt: 'pt_PT',
  ro: 'ro_RO',
  sl: 'sl_SI',
};

@Injectable({
  providedIn: 'root',
})
export class I18nService {
  defaultLanguage!: string;
  supportedLanguages!: string[];

  private langChangeSubscription!: Subscription;
  private _currLanguage = new BehaviorSubject<string>('it_IT');
  readonly currLanguage$ = this._currLanguage.asObservable();

  constructor(
    private translateService: TranslateService,
    private localStorageService: LocalStorageService,
    private _translatePipe: TranslatePipe
  ) {
    // Set default language
    this.translateService.setDefaultLang(DEFAULT_LANGUAGE);
  }

  init(browserLanguage: string, supportedLanguages: string[]) {
    this.supportedLanguages = supportedLanguages;

    // Forza l'italiano come lingua di default per l'accessibilità
    this.defaultLanguage = DEFAULT_LANGUAGE;

    // Set initial language from browser
    this.language = browserLanguage;

    // Warning: this subscription will always be alive for the app's lifetime
    this.langChangeSubscription = this.translateService.onLangChange.subscribe(
      (event: LangChangeEvent) => {
        this.localStorageService.store(languageKey, event.lang);
        this._currLanguage.next(event.lang);
      },
      error => {
        log.error('Error in language change subscription:', error);
      }
    );
  }

  set language(language: string) {
    if (language.length == 2) {
      language = languagesMap[language];
    }

    let newLanguage =
      language ||
      navigator.language ||
      this.localStorageService.retrieve(languageKey) ||
      this.translateService.getBrowserCultureLang() ||
      '';

    let isSupportedLanguage = this.supportedLanguages.includes(newLanguage);

    // Fallback if language is not supported
    if (!newLanguage || !isSupportedLanguage) {
      newLanguage = this.defaultLanguage;
    }

    language = newLanguage;

    log.debug(`Language set to ${language}`);
    this.translateService.use(language).subscribe({
      error: error => {
        log.error('Error setting language:', error);
      },
    });
  }

  get language(): string {
    return (
      this.translateService?.currentLang ||
      this.defaultLanguage ||
      DEFAULT_LANGUAGE
    );
  }

  get languageLabel(): string {
    const languageKey = `LANGUAGE.BUTTON_LANG_${this.get2charLang(
      this.language
    ).toUpperCase()}`;
    const languageName = this._translatePipe.transform(languageKey);
    return this._translatePipe.transform('LANGUAGE.SWITCH_LABEL', {
      language: languageName,
    });
  }

  get languages(): string[] {
    return this.supportedLanguages;
  }

  get languages2chars(): string[] {
    return this.supportedLanguages.map(lang => this.get2charLang(lang));
  }

  get languagesKeyTranslations(): string[] {
    return this.supportedLanguages.map(lang =>
      `LANGUAGE.BUTTON_LANG_${this.get2charLang(lang)}`.toUpperCase()
    );
  }

  private get2charLang(lang: string): string {
    return lang.split('_')[0];
  }
}
